import { useEffect, useState } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { toast } from "sonner";
import { Check, Download, Loader2, Trophy } from "lucide-react";
import { StreakBadge } from "./TargetCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const MEDAL = {
  1: ["bg-amber-50", "bg-gradient-to-br from-yellow-300 to-amber-500 text-amber-950", "Gold"],
  2: ["bg-slate-50", "bg-gradient-to-br from-slate-200 to-slate-400 text-slate-900", "Silver"],
  3: ["bg-orange-50/60", "bg-gradient-to-br from-orange-200 to-orange-400 text-orange-950", "Bronze"],
};
const thisMonth = () => new Date(Date.now() + 330 * 60000).toISOString().slice(0, 7);
const fmtDay = (d) => new Date(d + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" });

const today = () => new Date(Date.now() + 330 * 60000).toISOString().slice(0, 10);
const PERIODS = [["day", "Daily"], ["week", "Weekly"], ["month", "Monthly"]];
const EMPTY = { day: "No parcels started on this day yet.", week: "No parcels started in this week yet.", month: "No parcels started in this month yet." };

// Daily / weekly / monthly leaderboard — rank by target-met days, then total parcels. Visible to everyone.
export const Leaderboard = () => {
  const [period, setPeriod] = useState("day");
  const [month, setMonth] = useState(thisMonth());
  const [date, setDate] = useState(today());
  const [d, setD] = useState(null);
  const params = period === "month" ? { period, month } : { period, date };
  const pkey = JSON.stringify(params);
  useEffect(() => {
    setD(null);
    axios.get(`${API}/reports/leaderboard`, { headers: getAuthHeader(), params })
      .then(r => setD(r.data)).catch(e => { toast.error(e.response?.data?.detail || "Could not load leaderboard"); setD({ rows: [] }); });
  }, [pkey]);
  const download = async () => {
    try {
      const r = await axios.get(`${API}/reports/leaderboard/excel`, { headers: getAuthHeader(), params, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([r.data]));
      a.download = `Leaderboard_${d?.tag || month}.xlsx`;
      a.click();
    } catch { toast.error("Excel download failed"); }
  };
  const isDay = period === "day";
  const cols = isDay ? ["Rank", "Member", "Target met", "Parcels", "Streak", "Pending parcels"]
    : ["Rank", "Member", "Target-met days", "Total parcels", "Working days", "Avg / day", "Best day", "Streak", "Pending parcels"];
  return (
    <div className="p-4 pt-2 space-y-3" data-testid="leaderboard">
      <div className="flex flex-wrap items-center gap-2">
        <Trophy className="w-5 h-5 text-amber-500" />
        <span className="font-semibold text-slate-800" data-testid="leaderboard-label">{d?.label || "Leaderboard"}</span>
        <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden" role="group" data-testid="leaderboard-period">
          {PERIODS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setPeriod(k)} data-testid={`leaderboard-period-${k}`}
              className={`px-3 py-1 text-xs font-semibold transition-colors ${period === k ? "bg-amber-500 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{l}</button>
          ))}
        </div>
        {period === "month"
          ? <input type="month" value={month} onChange={e => e.target.value && setMonth(e.target.value)} data-testid="leaderboard-month"
              className="px-2 py-1 border border-slate-300 rounded-lg text-sm" />
          : <input type="date" value={date} onChange={e => e.target.value && setDate(e.target.value)} data-testid="leaderboard-date"
              title={period === "week" ? "Pick any day — the Monday–Sunday week containing it is shown" : "Pick a day"}
              className="px-2 py-1 border border-slate-300 rounded-lg text-sm" />}
        <span className="text-xs text-slate-500">{isDay ? "Ranked by target met, then parcels." : "Ranked by days at/above target, then total parcels."} All counts are parcels (a multi-product parcel counts once).</span>
        <button onClick={download} data-testid="leaderboard-excel-btn"
          className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white">
          <Download className="w-3.5 h-3.5" /> Excel
        </button>
      </div>
      {!d ? <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        : d.rows.length === 0 ? <div className="p-6 text-center text-sm text-slate-500" data-testid="leaderboard-empty">{EMPTY[period]}</div>
        : (
          <div className="overflow-x-auto">
            <table className={`w-full border-collapse text-sm ${isDay ? "min-w-[560px]" : "min-w-[820px]"}`} data-testid="leaderboard-table">
              <thead><tr className="bg-[#DDEBF7] text-xs">
                {cols.map(h => (
                  <th key={h} className={`px-3 py-2 border border-slate-200 ${h === "Member" ? "text-left" : ""}`}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {d.rows.map(r => {
                  const m = MEDAL[r.rank];
                  return (
                    <tr key={r.email} className={m ? m[0] : ""} data-testid={`leaderboard-row-${r.rank}`}>
                      <td className="px-3 py-2 border border-slate-200 text-center">
                        {m ? <span className={`inline-flex w-7 h-7 items-center justify-center rounded-full text-xs font-black shadow ${m[1]}`} title={m[2]} data-testid={`leaderboard-medal-${r.rank}`}>{r.rank}</span>
                          : <span className="text-slate-500 font-semibold">{r.rank}</span>}
                      </td>
                      <td className="px-3 py-2 border border-slate-200 font-semibold text-slate-800">{r.name}
                        {r.target ? <span className="ml-1 text-[10px] text-slate-400">target {r.target}</span> : <span className="ml-1 text-[10px] text-slate-400">no target</span>}</td>
                      <td className="px-3 py-2 border border-slate-200 text-center text-base font-black text-[#2F5496]" data-testid={`leaderboard-met-${r.rank}`}>
                        {isDay ? (r.target ? (r.met_days ? <Check className="w-5 h-5 text-emerald-600 inline" /> : <span className="text-slate-400 text-xs font-semibold">{Math.max(0, r.target - r.total_parcels)} short</span>) : "—") : r.met_days}</td>
                      <td className="px-3 py-2 border border-slate-200 text-center font-bold" data-testid={`leaderboard-parcels-${r.rank}`}>{r.total_parcels}</td>
                      {!isDay && <>
                        <td className="px-3 py-2 border border-slate-200 text-center">{r.working_days}</td>
                        <td className="px-3 py-2 border border-slate-200 text-center">{r.avg_per_day}</td>
                        <td className="px-3 py-2 border border-slate-200 text-center text-xs">{fmtDay(r.best_day)} · <b>{r.best_day_parcels}</b></td>
                      </>}
                      <td className="px-3 py-2 border border-slate-200 text-center">
                        {r.tier ? <StreakBadge r={r} testid={`leaderboard-badge-${r.rank}`} /> : <span className="text-xs text-slate-500">{r.streak ? `${r.streak} days` : "—"}</span>}
                      </td>
                      <td className="px-3 py-2 border border-slate-200 text-center" data-testid={`leaderboard-pending-${r.rank}`}
                          title="Pending parcels in this member's service + country rights (User Management)">
                        {r.pending == null ? <span className="text-xs text-slate-400">—</span> : <b className="text-amber-700">{r.pending}</b>}
                        {r.urgent > 0 && <span className="ml-1 text-[10px] font-bold bg-red-600 text-white rounded-full px-1.5">{r.urgent} urgent parcels</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
};

// Order Register entry point — team members don't have the Reports panel.
export const LeaderboardButton = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="or-leaderboard-btn"
        className="inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">
        <Trophy className="w-3.5 h-3.5" /> Leaderboard
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="leaderboard-dialog">
          <DialogHeader><DialogTitle>Leaderboard</DialogTitle></DialogHeader>
          {open && <Leaderboard />}
        </DialogContent>
      </Dialog>
    </>
  );
};
